/**
 * Offering popups for the diligence offerings map: one listing (hover preview or
 * pinned dialog) and a list for listings that share a map position. Advertised
 * prices stay primary; frozen GDV type adjustments are separately labelled.
 *
 * These components render the CONTENT of the overlay shell. The overlay
 * (services/mapPopup) and its controller (offeringMarkers) own the shell element
 *   <div class="dil-pop" data-mode="preview|pinned" data-side="above|below|right|left" style="--arrow: 48px; --x: …px; --y: …px">
 * and with it placement (placePopup), hover timing, pin marking and focus return.
 * The JSX here carries no style props.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { IconBath, IconBed, IconCheck, IconChevronLeft, IconChevronRight, IconExternalLink, IconHistory, IconX, IconZoomIn } from '@tabler/icons-react'
import { safeRightmoveUrl, type MarketOffering, type MarketTransactionType } from '../../../api/marketOfferings'
import { IconButton } from '../../../ui/kit'
import { useOverlay } from '../../../ui/overlays'
import { label as titleCase, miles, money, num, plain } from './format'

const RETAINED = 'Retained assessment advert · current availability unknown'

const placeOf = (offering: MarketOffering): string | null => offering.address || offering.postcode || null
const typeOf = (offering: MarketOffering): string | null => (offering.propertyType ? titleCase(offering.propertyType) : null)
const isRetained = (offering: MarketOffering): boolean => offering.observationBasis === 'frozen_assessment_advert'
/** The saved assessment's own figure, shown only when it differs from the advert price. */
const assessedPence = (offering: MarketOffering): number | null =>
  offering.usedInAssessment && offering.assessmentPricePence !== null && offering.assessmentPricePence !== offering.pricePence
    ? offering.assessmentPricePence : null
/** Assessment contributors first, then cheapest advert first; unknown prices last. Map stacks use the same order. */
export const byEvidence =(a: MarketOffering, b: MarketOffering): number =>
  Number(b.usedInAssessment) - Number(a.usedInAssessment)
  || (a.pricePence ?? Number.MAX_SAFE_INTEGER) - (b.pricePence ?? Number.MAX_SAFE_INTEGER)

/** "pcm" after a rent; screen readers hear "per month". */
function Per({ rent }: { rent: boolean }) {
  return rent ? <span className="dil-pop-per"><span aria-hidden="true">pcm</span><span className="sr-only"> per month</span></span> : null
}

function Price({ pence, rent }: { pence: number | null; rent: boolean }) {
  return pence === null ? <span className="is-missing">Price unknown</span> : <>{money(pence)}<Per rent={rent} /></>
}

function GdvTypeAdjustment({ offering, pinned }: { offering: MarketOffering; pinned: boolean }) {
  const adjustment = offering.gdvTypeAdjustment
  const equivalent = offering.assessmentAdjustedPricePence
  if (!adjustment || equivalent == null) return null
  const multiplier = Number(adjustment.ratio[0]) / Number(adjustment.ratio[1])
  const percent = Number.isFinite(multiplier) && multiplier > 0
    ? new Intl.NumberFormat('en-GB', { style: 'percent', maximumFractionDigits: 1, signDisplay: 'always' }).format(multiplier - 1) : null
  return <>
    <p className="dil-pop-assessed">GDV equivalent <b>{money(equivalent)}</b></p>
    {pinned ? <p className="dil-pop-note">Type adjustment{percent ? ` ${percent}` : ''}: {titleCase(adjustment.sourceType)} → {titleCase(adjustment.targetType)}. Based on sale asking prices and applied to the saved assessment advert.</p> : null}
  </>
}

/** The advert price comes from the retained assessment advert; the full sentence is in the title and the pinned detail. */
function RetainedMark() {
  return (
    <span className="dil-pop-retained" title={RETAINED}>
      <IconHistory className="ic" aria-hidden="true" /><span className="sr-only">, retained assessment advert</span>
    </span>
  )
}

/** A count whose gap reads as unknown, never as zero. */
function Count({ icon, value, noun, className = 'dil-pop-fact' }: {
  icon: ReactNode; value: number | null; noun: 'bedroom' | 'bathroom'; className?: string
}) {
  const spoken = value === null ? `${noun}s unknown` : `${plain(value)} ${noun}${value === 1 ? '' : 's'}`
  return (
    <span className={`${className}${value === null ? ' is-missing' : ''}`} title={spoken}>
      {icon}<span aria-hidden="true">{plain(value)}</span><span className="sr-only">{spoken}</span>
    </span>
  )
}

/** Pinned only: why the listing is on the map, the retained warning and the outbound link. */
function OfferingDetail({ offering }: { offering: MarketOffering }) {
  const url = safeRightmoveUrl(offering.rightmoveUrl)
  const { price, unitPrice } = offering.assessmentUses
  return (
    <div className="dil-pop-more">
      {!offering.usedInAssessment
        ? <p className="dil-pop-why">Additional match · not in assessment</p>
        : price || unitPrice
          ? (
            <p className="dil-pop-why is-used">
              Used for{' '}
              {price ? <span className="dil-pop-use">Total price</span> : null}
              {price && unitPrice ? <span className="sr-only"> and </span> : null}
              {unitPrice ? <span className="dil-pop-use">Price per area</span> : null}
            </p>
          )
          : <p className="dil-pop-why is-used">Used in assessment</p>}
      {isRetained(offering) ? <p className="dil-pop-note"><IconHistory className="ic" aria-hidden="true" />{RETAINED}</p> : null}
      {url
        ? (
          <a className="btn is-small dil-pop-link" href={url} target="_blank" rel="noopener noreferrer">
            Open listing<IconExternalLink className="ic" aria-hidden="true" /><span className="sr-only"> (opens in a new tab)</span>
          </a>
        )
        : <p className="dil-pop-note">Listing link unavailable</p>}
    </div>
  )
}

/**
 * Shared by the single card and a group's drill-in. In the preview, `echo` hides
 * what the pin's own aria-label already says (price, address, in assessment), so
 * the pin's aria-describedby adds only the new facts instead of repeating them.
 */
function OfferingBody({ offering, rent, pinned }: { offering: MarketOffering; rent: boolean; pinned: boolean }) {
  const echo = pinned ? undefined : true
  const place = placeOf(offering)
  const type = typeOf(offering)
  // Type truncates first in the preview; pinned adds tenure to the same wrapping text fact.
  const kind = [type ?? 'Type unknown', pinned && offering.tenure ? titleCase(offering.tenure.toLowerCase()) : null].filter(Boolean).join(' · ')
  const assessed = assessedPence(offering)
  return (
    <>
      <div className="dil-pop-head">
        <p className="dil-pop-price">
          <span aria-hidden={echo}><Price pence={offering.pricePence} rent={rent} /></span>
          {isRetained(offering) ? <RetainedMark /> : null}
        </p>
        {offering.usedInAssessment
          ? <span className="chip dil-pop-chip" title="Used in the saved assessment" aria-hidden={echo}><IconCheck className="ic" aria-hidden="true" />In assessment</span>
          : null}
      </div>
      {assessed !== null ? <p className="dil-pop-assessed">Assessment price <b>{money(assessed)}</b><Per rent={rent} /></p> : null}
      {!rent ? <GdvTypeAdjustment offering={offering} pinned={pinned} /> : null}
      <p className="dil-pop-facts">
        <Count icon={<IconBed className="ic" aria-hidden="true" />} value={offering.bedrooms} noun="bedroom" />
        <Count icon={<IconBath className="ic" aria-hidden="true" />} value={offering.bathrooms} noun="bathroom" />
        {offering.areaSqm !== null ? <span className="dil-pop-fact" title="Floor area">{num(offering.areaSqm, 0, ' m²')}<span className="sr-only"> floor area</span></span> : null}
        <span className={`dil-pop-fact is-text${type ? '' : ' is-missing'}`} title={kind}>{kind}</span>
      </p>
      <p className="dil-pop-where">
        <span className={`dil-pop-addr${place ? '' : ' is-missing'}`} title={place ?? undefined} aria-hidden={echo}>{place ?? 'Address unavailable'}</span>
        {offering.distanceMiles !== null
          ? <span className="dil-pop-dist" title="Distance from the property; positions may be postcode centres">{miles(offering.distanceMiles)}<span className="sr-only"> from the property</span></span>
          : null}
      </p>
      {pinned ? <OfferingDetail offering={offering} /> : null}
    </>
  )
}

/** One listing. The preview is a control-free glance; pinned is a labelled, non-modal dialog with Close. */
export function OfferingPopup({ offering, transactionType, pinned, onClose }: {
  offering: MarketOffering; transactionType: MarketTransactionType; pinned: boolean; onClose: () => void
}) {
  const root = useRef<HTMLDivElement>(null)
  // On the overlay stack: Escape dismisses the preview (WCAG 1.4.13) or the pinned card, unless a
  // popover, menu or dialog opened later is on top; that one answers first.
  useOverlay(true, onClose)
  // Pinning moves focus into the card; the controller returns it to the pin on close.
  useEffect(() => { if (pinned) root.current?.focus({ preventScroll: true }) }, [pinned, offering.id])
  const place = placeOf(offering)
  return (
    <div ref={root} className="dil-pop-offer" data-kind={transactionType} data-listing-id={offering.listingId}
      role={pinned ? 'dialog' : undefined} aria-label={pinned ? (place ?? 'Selected listing') : undefined} tabIndex={pinned ? -1 : undefined}>
      {pinned ? <IconButton small label="Close" className="dil-pop-close" onClick={onClose}><IconX className="ic" aria-hidden="true" /></IconButton> : null}
      <OfferingBody offering={offering} rent={transactionType === 'rent'} pinned={pinned} />
    </div>
  )
}

/** 2..N listings at one map position: a two-line price list; a row opens the full card in place. Always pinned. */
export function OfferingList({ offerings, transactionType, zoom, onClose }: {
  offerings: MarketOffering[]; transactionType: MarketTransactionType
  /** Pass only when the group separates at a closer zoom (it is not `tight`: members span more than 30 m). */
  zoom?: () => void
  onClose: () => void
}) {
  const rent = transactionType === 'rent'
  const rows = useMemo(() => [...offerings].sort(byEvidence), [offerings])
  const used = rows.filter(offering => offering.usedInAssessment).length
  const [openId, setOpenId] = useState<string | null>(null)
  const open = rows.find(offering => offering.id === openId) ?? null
  const openKey = open?.id ?? null
  const root = useRef<HTMLDivElement>(null)
  const detail = useRef<HTMLDivElement>(null)
  const rowRefs = useRef(new Map<string, HTMLButtonElement>())
  const lastRow = useRef<string | null>(null)
  const back = useCallback(() => setOpenId(null), [])
  // Escape steps back from a drill-in first, then closes the list. A stable callback keeps the
  // list's place on the overlay stack while drilling in and back.
  const openRef = useRef(open)
  openRef.current = open
  const escape = useCallback(() => (openRef.current ? back() : onClose()), [back, onClose])
  useOverlay(true, escape)
  // Mount: focus the dialog. Drill-in: focus the listing. Back: focus the row that was opened.
  useEffect(() => {
    if (openKey) { detail.current?.focus({ preventScroll: true }); return }
    const row = lastRow.current ? rowRefs.current.get(lastRow.current) : undefined
    if (row) { row.focus({ preventScroll: true }); row.scrollIntoView({ block: 'nearest' }) }
    else root.current?.focus({ preventScroll: true })
  }, [openKey])
  return (
    <div ref={root} className="dil-pop-group" data-kind={transactionType} role="dialog" tabIndex={-1}
      aria-label={`${rows.length} listings at this map position`}>
      {open ? (
        <>
          <div className="dil-pop-nav">
            <button type="button" className="btn is-small dil-pop-back" aria-label={`Back to ${rows.length} listings`} title="Back to the list" onClick={back}>
              <IconChevronLeft className="ic" aria-hidden="true" />{rows.length} listings
            </button>
            <IconButton small label="Close" onClick={onClose}><IconX className="ic" aria-hidden="true" /></IconButton>
          </div>
          <div ref={detail} className="dil-pop-offer is-detail" data-kind={transactionType} data-listing-id={open.listingId}
            role="group" aria-label={placeOf(open) ?? 'Selected listing'} tabIndex={-1}>
            <OfferingBody offering={open} rent={rent} pinned />
          </div>
        </>
      ) : (
        <div className="dil-pop-gh">
          <p className="dil-pop-gtitle">
            <span><b>{rows.length}</b> listings{used ? <> · <b>{used}</b> in assessment</> : null}</span>
            {zoom ? null : <small>Shared map position</small>}
          </p>
          {zoom ? <IconButton small label="Zoom in to separate listings" onClick={zoom}><IconZoomIn className="ic" aria-hidden="true" /></IconButton> : null}
          <IconButton small label="Close" onClick={onClose}><IconX className="ic" aria-hidden="true" /></IconButton>
        </div>
      )}
      {/* Kept mounted while a row is open so its scroll position and row refs survive Back. */}
      <ul className="dil-pop-list" hidden={open !== null}>
        {rows.map(offering => {
          const sub = `${typeOf(offering) ?? 'Type unknown'} · ${placeOf(offering) ?? 'Address unavailable'}`
          return (
            <li key={offering.id}>
              <button type="button" className={`dil-pop-row${offering.usedInAssessment ? ' is-used' : ''}`} data-listing-id={offering.listingId}
                ref={node => { if (node) rowRefs.current.set(offering.id, node); return () => { rowRefs.current.delete(offering.id) } }}
                onClick={() => { lastRow.current = offering.id; setOpenId(offering.id) }}>
                <span className="dil-pop-dot">
                  {offering.usedInAssessment ? <IconCheck className="ic" aria-hidden="true" /> : null}
                  <span className="sr-only">{offering.usedInAssessment ? 'In assessment: ' : 'Additional match: '}</span>
                </span>
                <span className="dil-pop-rowprice"><Price pence={offering.pricePence} rent={rent} />{isRetained(offering) ? <RetainedMark /> : null}</span>
                <Count className="dil-pop-rowbeds" icon={<IconBed className="ic" aria-hidden="true" />} value={offering.bedrooms} noun="bedroom" />
                <IconChevronRight className="ic dil-pop-go" aria-hidden="true" />
                <span className="dil-pop-rowsub" title={sub}>{sub}</span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
